import { FONTS } from 'common/constants';
import { ExpandFromType, PointPosType } from 'common/contracts';
import React from 'react';
import styled, { css, keyframes } from 'styled-components';

export interface IPointContainerProps extends React.HTMLAttributes<HTMLElement> {
  position: PointPosType;
  expandFrom?: ExpandFromType;
  isExpanded?: boolean;
}

const circle = css`
  border-radius: 50%;
  width: 22px;
  height: 22px;

  @media screen and (max-width: 1300px) {
    width: 15px;
    height: 15px;
  }
`;

const hotspotPop = keyframes`
    0% {
        visibility: hidden;
        opacity: 0;
        transform: translateX(-15px) translateY(-15px) scale(4);
    }
    100% {
        visibility: visible;
        opacity: 1;
        transform: translateX(-15px) translateY(-15px) scale(1);
  }
`;

const sonarAnim = keyframes`
  0% {
    opacity: 1;
    transform: scale(0);
  }

  100% {
    opacity: 0;
    transform: scale(4);
  }
`;

export const DirectionControlWrapper = styled.div<{ expandFrom: ExpandFromType }>`
  background: none;
  box-shadow: 0px 0px 16px 0px rgb(202, 202, 202, 0.4);
  border-radius: 12px;
  color: transparent;
  font-size: 14px;
  padding: 12px 0px 8px;
  white-space: nowrap;
  width: 0px;
  position: absolute;
  top: 0;
  transform: translateY(-50%);
  transition: all 0.3s cubic-bezier(0.18, 0.89, 0.42, 1.11);

  ${({ expandFrom }) => {
    if (expandFrom === 'left') {
      return css`
        left: 0;
      `;
    }
    if (expandFrom === 'right') {
      return css`
        right: 0;
      `;
    }
  }}
  @media screen and (max-width: 1300px) {
    border-radius: 6px;
    padding: 7px 0px 3px;
    font-size: 10px;
  }
`;

export const Marker = styled.div<{ sonarDelay: number; expandFrom: ExpandFromType }>`
  background: ${({ theme }) => theme.colors.primary};
  box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary};
  border: 4px solid #fff;
  position: absolute;
  transform: translateY(-2px);
  ${circle}

  ${({ expandFrom }) => {
    if (expandFrom === 'left') {
      return css`
        left: 15px;
        @media screen and (max-width: 1300px) {
          left: 10px;
        }
      `;
    }

    return css`
      right: 15px;
      @media screen and (max-width: 1300px) {
        right: 10px;
      }
    `;
  }}

  &:after {
    animation: ${sonarAnim} 1.4s ease-in-out infinite;
    animation-delay: ${({ sonarDelay }) => `${sonarDelay}s`};
    content: '';
    background: ${({ theme }) => theme.colors.primary};
    border-radius: 50%;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
  }
`;

const ExpandedPointMixing = (expandFrom: ExpandFromType) =>
  css`
  ${DirectionControlWrapper} {
    background: #fff;
    box-shadow: 0px 0px 16px 0px rgb(202, 202, 202, 0.4);
    color: inherit;
    font-family: ${FONTS.medium};
    font-size: 14px;
    font-weight: 600;
    width: auto;
    ${() => {
      if (expandFrom === 'right') {
        return css`
          padding: 12px 47px 8px 20px;

          @media screen and (max-width: 1300px) {
            font-size: 10px;
            padding: 7px 32px 3px 10px;
          }
        `;
      }

      return css`
        padding: 12px 20px 8px 47px;

        @media screen and (max-width: 1300px) {
          font-size: 10px;
          padding: 7px 10px 3px 32px;
        }
      `;
    }}
  `;

export const PointContainer = styled.div<IPointContainerProps>`
  animation: ${hotspotPop} 0.4s ease forwards;
  animation-delay: ${() => `${Math.random() + 0.1}s`};
  cursor: pointer;

  position: absolute;
  transform: translateX(-15px) translateY(-15px) scale(1);
  transition: all 0.3s cubic-bezier(0.18, 0.89, 0.42, 1.11);

  ${({ position: { top, left, unit } }) => css`
    top: ${top}${unit};
    left: ${left}${unit};
  `}

  ${({ isExpanded, expandFrom }) =>
    isExpanded &&
    css`
        &${ExpandedPointMixing(expandFrom || 'left')}
      `}

  ${({ expandFrom }) => css`
    :hover${ExpandedPointMixing(expandFrom || 'left')} ;
  `}
`;
